import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/** @typedef {object} Refs
 * @property {HTMLInputElement} cep
 * @property {HTMLButtonElement} consultar
 * @property {HTMLElement} resultados
 * @property {HTMLElement} [erro]
 */

/** @typedef {{ key: string }} CartCleanup */

/**
 * Calculadora de frete na página de produto (API Ajax do carrinho + ViaCEP).
 * Não usa cart/clear.js — só adiciona/remove linha temporária quando necessário.
 *
 * @extends {Component<Refs>}
 */
class FreteProduto extends Component {
  requiredRefs = ['cep', 'consultar', 'resultados'];

  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {Element | null} */
  #section = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('.freteprd__form');
    this.#form?.addEventListener('submit', this.#onFormSubmit);
    this.refs.cep.addEventListener('input', this.#onCepInput);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#form?.removeEventListener('submit', this.#onFormSubmit);
    this.refs.cep.removeEventListener('input', this.#onCepInput);
    this.#activeFetch?.abort();
    super.disconnectedCallback();
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const resource = detail?.resource;
        const id = this.#parseVariantId(resource?.id);
        if (id) this.dataset.variantId = String(id);
      })
      .catch(() => {});
  };

  /** @param {string | number | undefined | null} value */
  #parseVariantId(value) {
    if (value == null || value === '') return null;
    const str = String(value);
    const fromGid = str.match(/ProductVariant\/(\d+)/);
    if (fromGid) return parseInt(fromGid[1], 10);
    const num = parseInt(str, 10);
    return Number.isNaN(num) ? null : num;
  }

  #onFormSubmit = (event) => {
    event.preventDefault();
    this.#consultar();
  };

  #onCepInput = () => {
    const digits = this.#cepDigits(this.refs.cep.value);
    const masked =
      digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
    if (this.refs.cep.value !== masked) this.refs.cep.value = masked;
  };

  #cepDigits(value) {
    return value.replace(/\D/g, '').slice(0, 8);
  }

  #setErro(message) {
    if (!this.refs.erro) return;
    if (!message) {
      this.refs.erro.hidden = true;
      this.refs.erro.textContent = '';
      return;
    }
    this.refs.erro.hidden = false;
    this.refs.erro.textContent = message;
  }

  #root() {
    return this.closest('.freteprd');
  }

  #setLoading(loading) {
    this.refs.consultar.disabled = loading;
    this.refs.consultar.setAttribute('aria-busy', loading ? 'true' : 'false');
    this.#root()?.classList.toggle('freteprd--carregando', loading);
  }

  #abrirResultados() {
    this.#root()?.classList.add('freteprd--aberto');
  }

  #renderCards(rates) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    const gratisLabel = this.dataset.textoGratis || 'Grátis';

    this.refs.resultados.hidden = false;
    this.refs.resultados.innerHTML = '';

    for (const rate of rates) {
      const card = document.createElement('div');
      card.className = 'freteprd__card';

      const nome = document.createElement('p');
      nome.className = 'freteprd__card-nome';
      nome.textContent = rate.presentment_name || rate.name || '';
      card.appendChild(nome);

      const preco = document.createElement('p');
      preco.className = 'freteprd__card-preco';
      const priceNum = parseFloat(rate.price);
      if (!Number.isNaN(priceNum) && priceNum === 0) {
        preco.textContent = gratisLabel;
      } else if (!Number.isNaN(priceNum)) {
        const minor =
          convertMoneyToMinorUnits(String(priceNum), currency) ?? Math.round(priceNum * 100);
        preco.textContent = formatMoney(minor, moneyFormat, currency);
      }
      card.appendChild(preco);

      const prazoText = this.#formatPrazo(rate);
      if (prazoText) {
        const prazo = document.createElement('p');
        prazo.className = 'freteprd__card-prazo';
        prazo.textContent = prazoText;
        card.appendChild(prazo);
      }

      this.refs.resultados.appendChild(card);
    }

    this.#abrirResultados();
  }

  /** @param {Record<string, unknown>} rate */
  #formatPrazo(rate) {
    const prefix = this.dataset.textoPrazoPrefixo || '';

    if (rate.delivery_date && typeof rate.delivery_date === 'string') {
      const date = new Date(rate.delivery_date);
      if (!Number.isNaN(date.getTime())) {
        const formatted = date.toLocaleDateString('pt-BR');
        return prefix ? `${prefix} ${formatted}` : `Previsto para ${formatted}`;
      }
    }

    const days = rate.delivery_days;
    if (Array.isArray(days) && days.length > 0) {
      const nums = days.map((d) => Number(d)).filter((n) => !Number.isNaN(n));
      if (nums.length) {
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        const range = min === max ? `${min}` : `${min}–${max}`;
        const base = `Prazo de entrega: ${range} dias úteis.`;
        return prefix ? `${prefix} ${base}` : base;
      }
    }

    if (typeof rate.description === 'string' && rate.description.trim()) {
      const desc = rate.description.trim();
      return prefix ? `${prefix} ${desc}` : desc;
    }

    return '';
  }

  async #fetchViaCep(cep) {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
      signal: this.#activeFetch?.signal,
    });
    if (!response.ok) throw new Error('viacep');
    const data = await response.json();
    if (data.erro) throw new Error('cep_not_found');
    return data;
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      signal: this.#activeFetch?.signal,
    });
    if (!response.ok) throw new Error('cart_fetch');
    return response.json();
  }

  /** @param {number} variantId @param {number} quantity */
  async #addVariant(variantId, quantity) {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      signal: this.#activeFetch?.signal,
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'cart_add');
    }
  }

  /** @param {string} lineKey */
  async #removeLine(lineKey) {
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
      signal: this.#activeFetch?.signal,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'cart_change');
    }
  }

  /**
   * Garante que o carrinho tenha a variante para cotação, sem limpar o carrinho.
   * @returns {Promise<CartCleanup | null>}
   */
  async #prepareCartForRates(variantId, quantity) {
    const cart = await this.#fetchCart();
    const items = cart.items || [];

    if (items.length === 0) {
      await this.#addVariant(variantId, quantity);
      const updated = await this.#fetchCart();
      const line = updated.items?.find(
        (/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId
      );
      return line?.key ? { key: line.key } : null;
    }

    const hasVariant = items.some(
      (/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId
    );
    if (hasVariant) return null;

    await this.#addVariant(variantId, quantity);
    const updated = await this.#fetchCart();
    const line = updated.items?.find(
      (/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId
    );
    return line?.key ? { key: line.key } : null;
  }

  /** @param {URLSearchParams} params */
  async #fetchShippingRates(params) {
    const base = Theme.routes.cart_url;
    const query = params.toString();
    const pollDelayMs = 400;
    const maxAttempts = 15;

    const prepareResponse = await fetch(`${base}/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: this.#activeFetch?.signal,
    });
    if (!prepareResponse.ok) throw new Error('rates_failed');

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await fetch(`${base}/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
        signal: this.#activeFetch?.signal,
      });

      if (response.ok) {
        const payload = await response.json();
        if (payload.shipping_rates?.length) return payload.shipping_rates;
        if (payload.error) throw new Error(payload.error);
      }

      // 404/422 etc. = cotação ainda em processamento — seguir polling (não falhar na 1ª tentativa)
      await new Promise((resolve) => window.setTimeout(resolve, pollDelayMs));
    }

    const fallback = await fetch(`${base}/shipping_rates.json?${query}`, {
      headers: { Accept: 'application/json' },
      signal: this.#activeFetch?.signal,
    });
    if (fallback.ok) {
      const payload = await fallback.json();
      if (payload.shipping_rates?.length) return payload.shipping_rates;
    }

    throw new Error('rates_timeout');
  }

  #getQuantity() {
    const minQty = parseInt(this.dataset.quantityDefault || '1', 10);
    const min = Number.isNaN(minQty) || minQty < 1 ? 1 : minQty;

    const root = this.closest('.product-details') || document;
    const qtyInput = root.querySelector('input[name="quantity"]');
    if (qtyInput instanceof HTMLInputElement) {
      const parsed = parseInt(qtyInput.value, 10);
      if (!Number.isNaN(parsed) && parsed > 0) return Math.max(parsed, min);
    }
    return min;
  }

  #consultar = async () => {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();

    this.#setErro('');
    const cep = this.#cepDigits(this.refs.cep.value);

    if (cep.length !== 8) {
      this.#setErro(this.dataset.msgCepInvalido || 'Informe um CEP válido com 8 dígitos.');
      return;
    }

    const variantId = this.#parseVariantId(this.dataset.variantId);
    if (!variantId) {
      this.#setErro(this.dataset.msgSemVariante || 'Selecione uma variante do produto.');
      return;
    }

    const quantity = this.#getQuantity();
    this.#setLoading(true);
    this.refs.resultados.hidden = false;
    this.refs.resultados.innerHTML = '';

    /** @type {CartCleanup | null} */
    let cleanup = null;

    try {
      const viaCep = await this.#fetchViaCep(cep);
      const uf = viaCep.uf;
      if (!uf) throw new Error('cep_not_found');

      cleanup = await this.#prepareCartForRates(variantId, quantity);

      const cepFormatted = `${cep.slice(0, 5)}-${cep.slice(5)}`;
      const params = new URLSearchParams({
        'shipping_address[zip]': cepFormatted,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': uf,
      });
      if (viaCep.localidade) {
        params.set('shipping_address[city]', viaCep.localidade);
      }

      const rates = await this.#fetchShippingRates(params);

      if (!rates.length) {
        this.#setErro(this.dataset.msgSemOpcoes || 'Nenhuma opção de entrega para este CEP.');
        return;
      }

      this.#renderCards(rates);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      if (error instanceof Error) {
        if (error.message === 'cep_not_found') {
          this.#setErro(this.dataset.msgCepNaoEncontrado || 'CEP não encontrado.');
        } else if (error.message === 'cart_add') {
          this.#setErro(this.dataset.msgErroGenerico || 'Não foi possível calcular o frete. Tente novamente.');
        } else if (error.message === 'rates_timeout' || error.message === 'rates_failed') {
          this.#setErro(this.dataset.msgErroGenerico || 'Não foi possível calcular o frete. Tente novamente.');
        } else if (error.message.includes('cart') || error.message.includes('estoque')) {
          this.#setErro(error.message);
        } else {
          this.#setErro(this.dataset.msgErroGenerico || 'Não foi possível calcular o frete. Tente novamente.');
        }
      }
    } finally {
      if (cleanup?.key) {
        try {
          await this.#removeLine(cleanup.key);
        } catch {
          /* não bloqueia resultado exibido */
        }
      }
      this.#setLoading(false);
    }
  };
}

if (!customElements.get('frete-produto')) {
  customElements.define('frete-produto', FreteProduto);
}
